import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { RunFacetField } from '@/types/schema';

const GROUP_BY_KEY_PARAM = 'groupByKey';
const GROUP_BY_VALUE_PARAM = 'groupByValue';

// Serialization helpers
function serializeMetadataKey(key: RunFacetField | null): {
  key: string | null;
  value: string | null;
} {
  if (!key) return { key: null, value: null };
  return { key: key.key, value: key.value };
}

function deserializeMetadataKey(params: URLSearchParams): RunFacetField | null {
  const key = params.get(GROUP_BY_KEY_PARAM);
  const value = params.get(GROUP_BY_VALUE_PARAM);
  if (!key || !value) return null;
  return { key, value, query: `${key}:${value}` };
}

// Hook that manages metadata key in URL
export function useGroupByMetadata() {
  const [searchParams, setSearchParams] = useSearchParams();

  const metadataKey = deserializeMetadataKey(searchParams);

  const setMetadataKey = useCallback(
    (newKey: RunFacetField | null) => {
      const { key, value } = serializeMetadataKey(newKey);

      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          if (key && value) {
            newParams.set(GROUP_BY_KEY_PARAM, key);
            newParams.set(GROUP_BY_VALUE_PARAM, value);
          } else {
            newParams.delete(GROUP_BY_KEY_PARAM);
            newParams.delete(GROUP_BY_VALUE_PARAM);
          }
          return newParams;
        },
        { replace: true } // Replace instead of push to avoid adding to history
      );
    },
    [setSearchParams]
  );

  return {
    metadataKey,
    setMetadataKey,
  };
}
